/**
 * Search overlay (predictive search) — sections/search-drawer.liquid.
 *
 * Owns the overlay: open/close (from header [data-search-trigger]), scroll-lock
 * + focus trap, and the debounced predictive fetch that swaps the presets for
 * live results.
 *
 * Results come from Shopify's Predictive Search section-rendering endpoint
 * (/search/suggest?...&section_id=predictive-search → sections/predictive-search
 * .liquid). We pull the inner [data-predictive-inner] out of the response and
 * drop it into [data-search-results]. Products first, then articles (no pages).
 *
 * Open/close/inert/focus-trap mirror assets/cart.js so the two drawers behave
 * identically.
 */

(function () {
  'use strict';

  if (window.__notedSearchInit) return;
  window.__notedSearchInit = true;

  var FALLBACK_ID = 'search-drawer';
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 250;

  var isOpen = false;
  var lastTrigger = null;
  var restoringFocus = false;
  var closeTimer = null;
  var debounceTimer = null;
  var reqToken = 0;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function drawerEl() { return document.querySelector('[data-search-drawer]'); }
  function sectionId() {
    var d = drawerEl();
    return (d && d.dataset.sectionId) || FALLBACK_ID;
  }
  function sectionWrapper() { return document.getElementById('shopify-section-' + sectionId()); }
  function inputEl() { var d = drawerEl(); return d && d.querySelector('[data-search-input]'); }
  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ───────────── Open / close ───────────── */

  function focusables(panel) {
    return Array.prototype.filter.call(
      panel.querySelectorAll('a[href],button:not([disabled]),input:not([type="hidden"]),select,textarea,[tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetWidth || el.offsetHeight || el.getClientRects().length; }
    );
  }

  function setBackgroundInert(on) {
    var wrap = sectionWrapper();
    var kids = document.body.children;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el === wrap) continue;
      if (on) { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); }
      else { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); }
    }
  }

  function open() {
    var d = drawerEl();
    if (!d || isOpen) return;
    isOpen = true;
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    d.hidden = false;
    void d.offsetWidth; // reflow so the transition runs from the closed state
    d.classList.add('is-open');
    document.body.classList.add('noted-drawer-open');
    document.querySelectorAll('[data-search-trigger]').forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
    setBackgroundInert(true);
    var input = inputEl();
    if (input) { try { input.focus(); } catch (e) {} }
  }

  function close() {
    var d = drawerEl();
    if (!d || !isOpen) return;
    isOpen = false;
    d.classList.remove('is-open');
    document.body.classList.remove('noted-drawer-open');
    document.querySelectorAll('[data-search-trigger]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    setBackgroundInert(false);
    // Hand focus back to what opened the overlay (the mobile search icon is a
    // button, so it takes focus; the header rows are forms, which do not). The
    // flag stops the focusin listener below treating that as a fresh "open":
    // without it a button trigger re-opened the overlay in the same tick, the
    // close timer then hid it, and the page was left scroll-locked and inert.
    if (lastTrigger) {
      var returnTo = lastTrigger;
      lastTrigger = null;
      restoringFocus = true;
      try { returnTo.focus({ preventScroll: true }); } catch (e) {}
      restoringFocus = false;
    }
    var delay = reducedMotion() ? 0 : 360;
    closeTimer = setTimeout(function () { d.hidden = true; }, delay);
  }

  /* ───────────── State toggles ───────────── */

  function show(el, on) { if (el) el.hidden = !on; }

  function showPresets() {
    var d = drawerEl(); if (!d) return;
    show(d.querySelector('[data-search-loading]'), false);
    show(d.querySelector('[data-search-results]'), false);
    show(d.querySelector('[data-search-presets]'), true);
  }
  function showLoading() {
    var d = drawerEl(); if (!d) return;
    show(d.querySelector('[data-search-presets]'), false);
    show(d.querySelector('[data-search-results]'), false);
    show(d.querySelector('[data-search-loading]'), true);
  }
  function showResults() {
    var d = drawerEl(); if (!d) return;
    show(d.querySelector('[data-search-presets]'), false);
    show(d.querySelector('[data-search-loading]'), false);
    show(d.querySelector('[data-search-results]'), true);
  }

  /* ───────────── Predictive fetch ───────────── */

  function runSearch(q) {
    q = (q || '').trim();
    if (q.length < MIN_CHARS) { showPresets(); return; }

    showLoading();
    var token = ++reqToken;
    var url = root() + 'search/suggest?q=' + encodeURIComponent(q) +
      '&resources[type]=product,article' +
      '&resources[limit]=10' +
      '&resources[limit_scope]=each' +
      '&section_id=predictive-search';

    fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('suggest ' + r.status); return r.text(); })
      .then(function (html) {
        if (token !== reqToken) return; // a newer keystroke superseded this one
        var d = drawerEl();
        var results = d && d.querySelector('[data-search-results]');
        if (!results) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var inner = parsed.querySelector('[data-predictive-inner]');
        results.innerHTML = inner ? inner.innerHTML : '';
        var body = d.querySelector('[data-search-body]');
        if (body) body.scrollTop = 0;
        showResults();
      })
      .catch(function (err) {
        if (token !== reqToken) return;
        console.error('[search] suggest failed', err);
        showPresets();
      });
  }

  function onInput() {
    var input = inputEl();
    if (!input) return;
    var q = input.value;
    if (debounceTimer) clearTimeout(debounceTimer);
    if (q.trim().length < MIN_CHARS) { showPresets(); return; }
    debounceTimer = setTimeout(function () { runSearch(q); }, DEBOUNCE_MS);
  }

  function clearInput() {
    var input = inputEl();
    if (!input) return;
    if (!input.value) { close(); return; } // empty already → close
    input.value = '';
    showPresets();
    try { input.focus(); } catch (e) {}
  }

  /* ───────────── Events ───────────── */

  // Open from the header search affordances; carry any typed value across.
  function openFromTrigger(trigger) {
    lastTrigger = trigger;
    open();
    var src = trigger.matches('input') ? trigger : trigger.querySelector('input[type="search"]');
    var input = inputEl();
    if (input && src && src.value) {
      input.value = src.value;
      runSearch(src.value);
    } else {
      showPresets();
    }
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-search-trigger]');
    if (trigger) { e.preventDefault(); openFromTrigger(trigger); return; }
    if (e.target.closest('[data-search-clear]')) { e.preventDefault(); clearInput(); return; }
    if (e.target.closest('[data-search-close]')) { e.preventDefault(); close(); return; }
  });

  // Header input focus → open (before the caret even lands). Only the read-only
  // inputs inside the header rows open on focus; a button trigger (the mobile
  // search icon) opens on click, so tabbing past it or getting focus back on
  // close never opens the overlay.
  document.addEventListener('focusin', function (e) {
    if (isOpen || restoringFocus) return;
    if (!e.target.matches || !e.target.matches('input')) return;
    var trigger = e.target.closest('[data-search-trigger]');
    if (trigger) openFromTrigger(trigger);
  });

  // Typing in the drawer input → debounced predictive fetch.
  document.addEventListener('input', function (e) {
    if (e.target.closest('[data-search-input]')) onInput();
  });

  // Enter submits the form natively to the results page (templates/search.json →
  // sections/main-search.liquid). It was suppressed while that page was still a
  // placeholder, which left Enter doing nothing at all; the page exists now, so
  // the drawer hands off to it. An empty box stays put rather than navigating to
  // a resultless page.
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-search-form]');
    if (!form) return;
    var input = form.querySelector('[data-search-input]');
    if (input && !input.value.trim()) e.preventDefault();
  });

  // ESC to close + Tab focus trap.
  document.addEventListener('keydown', function (e) {
    if (!isOpen) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'Tab') {
      var d = drawerEl();
      var panel = d && d.querySelector('.search-drawer__panel');
      if (!panel) return;
      var f = focusables(panel);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    }
  });
})();
